import { ApiResponse } from "@/@types/general";
import { ToastParams } from "./alert";
import { z } from "zod";

// utils/handleApiError.ts
type ToastFunction = (opts: ToastParams) => void;

export function handleApiSuccess<T extends object>(data: any): ApiResponse<T> {
  const response = {
    meta: {
      message: data.meta.message,
      code: data.meta.code,
      status: data.meta.status,
      success: true,
    },
    data: data.data,
  };
  return response;
}

export async function handleApiError(error: unknown, showToast: ToastFunction) {
  if (error instanceof Response) {
    try {
      const errorBody = await error.json();
      // console.log("API Error Body:", errorBody); // ✅ Debug
      showToast({
        title: errorBody?.message || "Something went wrong",
        icon: "error",
      });
    } catch (err) {
      // console.error("Failed to parse Response:", err);
      showToast({ title: "Something went wrong", icon: "error" });
    }
  } else {
    // console.error("Unexpected Error:", error);
    if (typeof error == "string") {
      showToast({ title: error || "Unexpected error occurred", icon: "error" });
    } else {
      showToast({ title: "Unexpected error occurred", icon: "error" });
    }
  }
}

export async function handleAPIResponseError(
  response: Response
): Promise<void | boolean> {
  if (!response.ok) {
    let errorData: any;

    try {
      errorData = await response.json();
      console.log({ errorData });

      errorData = {
        message: errorData.data?.message || errorData.meta?.message || "",
        data: errorData?.data!,
        timestamp: new Date(),
        status: response.status,
      };
    } catch (e: any) {
      console.log(e.toString());
      // In case the response body isn't JSON
      errorData = {
        message: response.statusText,
        data: null,
        timestamp: new Date(),
        status: response.status,
      };
    }
    console.log({ errorData });
    throw new Response(JSON.stringify(errorData), {
      status: response.status,
      statusText: errorData.message as string,
      headers: { "Content-Type": "application/json" },
    });
  }
}

export async function handleServerError(
  error: unknown,
  fallbackMessage = "An unexpected error occurred"
) {
  if (error instanceof z.ZodError) {
    return {
      meta: {
        message: "Validation Error",
        code: 400,
        status: "error",
        success: false,
      },
      data: null,
    };
  }

  let message: string =
    error instanceof Error ? error.message : fallbackMessage;
  let status = 500;
  let data = null;

  if (error instanceof Response) {
    try {
      const json = await error.json();
      message = json?.data?.error || json?.message || fallbackMessage;
      status = error.status;
      data = json.data;
    } catch (e) {
      console.error("Error parsing backend response", e);
    }
  }

  console.error("Server Action Error:", message);
  return {
    meta: {
      message,
      code: status,
      status: "error",
      success: false,
    },
    data,
  };
}
